import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { auth, provider } from '../components/FirebaseConfig';
import { signInWithPopup } from 'firebase/auth';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Segoe UI", sans-serif',
  },
});

function MonitorLogin({
  setUser,
  mode = 'guest', // 'guest' | 'remote'
  customBadge,
  customTitle,
  customSubtitle,
  customIcon,
  customDevText,
  customFooterText,
}) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

  const isRemote = mode === 'remote';
  const displayBadge = customBadge || (isRemote ? 'Control Remoto Anfitrión' : 'Mobile Companion');
  const displayTitle = customTitle || (isRemote ? 'Garaoke Remote' : 'Garaoke Live');
  const displaySubtitle = customSubtitle || (
    isRemote
      ? 'Inicia sesión con tu cuenta de Garaoke para vincular tu smartphone a la pantalla principal y controlar la fiesta en tiempo real.'
      : 'Conéctate a la sesión en vivo para seguir las pistas en tu móvil y calificar las canciones en tiempo real.'
  );
  const displayIcon = customIcon || (isRemote ? '📱' : '🎤');
  const displayDevText = customDevText || (isRemote ? '🚀 Ingresar como Anfitrión (Modo Dev)' : '🚀 Ingresar como Invitado (Modo Dev)');
  const displayFooterText = customFooterText || (
    isRemote
      ? '⚡ Vinculado a tu pantalla principal para control remoto'
      : '⚡ Sincronizado en tiempo real con la pantalla principal'
  );

  const handleGoogleSignIn = () => {
    setLoading(true);
    setErrorMsg('');

    if (isDevMode && isRemote) {
      handleDevLogin();
      setLoading(false);
      return;
    }

    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;
        setUser(user);
      })
      .catch((err) => {
        console.error('Error al iniciar sesión con Google:', err);
        const code = err.code || '';
        const currentHost = window.location.hostname;

        if (code === 'auth/unauthorized-domain') {
          setErrorMsg(
            `⚠️ Dominio no autorizado (${currentHost}). Debes agregar "${currentHost}" en Firebase Console > Authentication > Ajustes > Dominios autorizados.`
          );
        } else if (code === 'auth/popup-blocked') {
          setErrorMsg('⚠️ El navegador de tu celular bloqueó la ventana emergente de Google. Habilita las ventanas emergentes en tu navegador.');
        } else if (code === 'auth/popup-closed-by-user') {
          setErrorMsg('Ventana cerrada antes de completar el inicio de sesión.');
        } else if (code === 'auth/network-request-failed') {
          setErrorMsg('Error de red. Verifica que tu celular esté conectado a la misma red Wi-Fi.');
        } else {
          setErrorMsg(`No se pudo iniciar sesión (${code || err.message || 'error desconocido'}).`);
        }
        setLoading(false);
      });
  };


  const handleDevLogin = () => {
    if (isRemote) {
      const mockHost = {
        uid: import.meta.env.VITE_DEV_USER_ID || '7341be33-5da0-430a-8648-d3c54b97cfff',
        displayName: import.meta.env.VITE_DEV_USER_NAME || 'Anfitrión Dev',
        email: import.meta.env.VITE_DEV_USER_EMAIL || 'dev@garaoke.dev',
        photoURL: null,
      };
      setUser(mockHost);
    } else {
      const mockUser = {
        uid: 'dev-guest-' + Math.floor(Math.random() * 10000),
        displayName: 'Invitado ' + Math.floor(Math.random() * 90 + 10),
        email: 'guest@garaoke.dev',
        photoURL: null,
      };
      setUser(mockUser);
    }
  };


  const googlePngIcon = '/assets/images/google-icon32.png';

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'radial-gradient(circle at 50% 15%, rgba(180, 77, 255, 0.18) 0%, rgba(11, 12, 20, 0.98) 70%), #0b0c14',
          padding: '24px 16px',
          position: 'relative',
          overflow: 'hidden',
          fontFamily: '"Outfit", "Inter", sans-serif',
        }}
      >
        {/* Glows ambientales de fondo */}
        <Box
          sx={{
            position: 'absolute',
            top: '-80px',
            right: '-60px',
            width: '260px',
            height: '260px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 45, 155, 0.22) 0%, transparent 70%)',
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-100px',
            left: '-60px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.18) 0%, transparent 70%)',
            filter: 'blur(50px)',
            pointerEvents: 'none',
          }}
        />

        {/* Tarjeta Gamer Glassmorphism */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '380px',
            background: 'rgba(18, 20, 32, 0.75)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderTop: '1px solid rgba(255, 45, 155, 0.4)',
            borderBottom: '1px solid rgba(0, 212, 255, 0.4)',
            borderRadius: '24px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.6), 0 0 30px rgba(180, 77, 255, 0.15)',
            padding: '36px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            zIndex: 2,
          }}
        >
          {/* Badge Neón Superior */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.15), rgba(0, 212, 255, 0.15))',
              border: '1px solid rgba(0, 212, 255, 0.35)',
              boxShadow: '0 0 12px rgba(0, 212, 255, 0.2)',
              marginBottom: '20px',
            }}
          >
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#00d4ff',
                boxShadow: '0 0 8px #00d4ff',
                animation: 'pulseGlow 1.8s infinite',
                '@keyframes pulseGlow': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.5, transform: 'scale(1.3)' },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#00d4ff',
                fontSize: '0.74rem',
              }}
            >
              {displayBadge}
            </Typography>
          </Box>

          {/* Icono Gamer */}
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff2d9b, #9d4edd)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 24px rgba(255, 45, 155, 0.5)',
              marginBottom: '16px',
              fontSize: '30px',
            }}
          >
            {displayIcon}
          </Box>

          {/* Título de Bienvenida */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: '1.75rem',
              background: 'linear-gradient(135deg, #ffffff 40%, #00d4ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              letterSpacing: '-0.5px',
            }}
          >
            {displayTitle}
          </Typography>

          {/* Descripción */}
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '0.88rem',
              lineHeight: 1.5,
              marginBottom: '28px',
              paddingX: '8px',
            }}
          >
            {displaySubtitle}
          </Typography>

          {/* Mensaje de Error si ocurre */}
          {errorMsg && (
            <Typography
              variant="caption"
              sx={{
                color: '#ff4d6d',
                background: 'rgba(255, 77, 109, 0.12)',
                border: '1px solid rgba(255, 77, 109, 0.3)',
                borderRadius: '8px',
                padding: '6px 12px',
                marginBottom: '16px',
                display: 'block',
                width: '100%',
              }}
            >
              {errorMsg}
            </Typography>
          )}

          {/* Botón Principal Google Sign-In */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #ff2d9b 0%, #7b2cbf 100%)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              borderRadius: '14px',
              padding: '12px 20px',
              boxShadow: '0 4px 20px rgba(255, 45, 155, 0.45)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.25s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff4d6d 0%, #9d4edd 100%)',
                boxShadow: '0 6px 26px rgba(255, 45, 155, 0.65)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(1px)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: '#ffffff' }} />
            ) : (
              <>
                <img
                  src={googlePngIcon}
                  alt="Google"
                  width="20"
                  height="20"
                  style={{ borderRadius: '50%', background: '#fff', padding: '1px' }}
                />
                <span>Identifícate con Google</span>
              </>
            )}
          </Button>

          {/* Botón rápido modo DEV */}
          {isDevMode && (
            <Button
              variant="outlined"
              fullWidth
              onClick={handleDevLogin}
              sx={{
                marginTop: '12px',
                borderColor: 'rgba(0, 212, 255, 0.4)',
                color: '#00d4ff',
                fontSize: '0.82rem',
                textTransform: 'none',
                borderRadius: '12px',
                padding: '8px 16px',
                background: 'rgba(0, 212, 255, 0.05)',
                '&:hover': {
                  borderColor: '#00d4ff',
                  background: 'rgba(0, 212, 255, 0.15)',
                },
              }}
            >
              {displayDevText}
            </Button>
          )}

          {/* Pie de tarjeta con indicador de sincronización */}
          <Box
            sx={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '6px',
              color: 'rgba(255, 255, 255, 0.45)',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.74rem' }}>
              {displayFooterText}
            </Typography>
          </Box>

        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default MonitorLogin;
